import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { WORD_TYPES, wordTypeLabel } from "../../domain/vocabulary/types.ts";
import { findMeaningById, findWordById, groupWords } from "../../domain/vocabulary/grouping.ts";
import { relationTargetLabel, relationsFrom } from "../../domain/vocabulary/relations.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useOnline, useVocabularySnapshot } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function EditMeaningScreen() {
  const { meaningId = "" } = useParams();
  const snapshot = useVocabularySnapshot();
  const { commands } = useAppServices();
  const navigate = useNavigate();
  const online = useOnline();
  const meaning = findMeaningById(snapshot, meaningId);
  const word = meaning ? findWordById(snapshot, meaning.wordId) : undefined;
  const initialRelations = meaning ? relationsFrom(snapshot, meaning.id) : [];

  const [displayText, setDisplayText] = useState(word?.displayText ?? "");
  const [type, setType] = useState(meaning?.type ?? "other");
  const [shortLabel, setShortLabel] = useState(meaning?.shortLabel ?? "");
  const [definition, setDefinition] = useState(meaning?.definition ?? "");
  const [hint, setHint] = useState(meaning?.russianHint ?? "");
  const [example, setExample] = useState(meaning?.example ?? "");
  const [memoryKey, setMemoryKey] = useState(meaning?.memoryKey ?? "");
  const [collocations, setCollocations] = useState(meaning?.collocations ?? []);
  const [pattern, setPattern] = useState("");
  const [relations, setRelations] = useState(
    initialRelations.map((item) => ({
      targetMeaningId: item.targetMeaningId,
      explanation: item.explanation,
    })),
  );
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState<string>();

  const searchHits = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return [];
    }
    return groupWords(snapshot)
      .flatMap((group) =>
        group.meanings
          .filter((item) => item.id !== meaningId)
          .filter((item) => {
            const label = item.shortLabel.toLowerCase();
            const wordText = group.word.displayText.toLowerCase();
            return label.includes(needle) || wordText.includes(needle) || group.word.normalizedText.includes(needle);
          })
          .map((item) => ({
            id: item.id,
            label: `${group.word.displayText} · ${item.shortLabel}`,
            type: item.type,
          })),
      )
      .slice(0, 8);
  }, [search, snapshot, meaningId]);

  if (!meaning || !word) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="Edit Word" backTo="/words" />
          <div className={styles.empty}>This meaning is no longer saved.</div>
        </Screen>
      </Frame>
    );
  }

  const editable = meaning;

  async function save() {
    const result = await commands.saveEditedMeaning(
      editable.id,
      {
        displayText,
        type,
        shortLabel,
        definition,
        russianHint: hint || null,
        example,
        memoryKey: memoryKey || null,
        collocations,
      },
      relations,
    );
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    navigate(`/meanings/${editable.id}`);
  }

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar
          title="Edit Word"
          backTo={`/meanings/${meaning.id}`}
          right={
            <button className={styles.textButton} type="button" onClick={() => void save()}>
              Save
            </button>
          }
        />
        {message ? <p className={styles.error}>{message}</p> : null}
        {!online ? (
          <div className={styles.banner}>
            You can edit offline. If the meaning changes, practice waits until you regenerate exercises online.
          </div>
        ) : null}
        <label className={styles.field}>
          Word or phrase
          <input className={styles.input} value={displayText} onChange={(event) => setDisplayText(event.target.value)} />
        </label>
        <label className={styles.field}>
          Type
          <select className={styles.select} value={type} onChange={(event) => setType(event.target.value as typeof type)}>
            {WORD_TYPES.map((item) => (
              <option key={item} value={item}>
                {wordTypeLabel(item)}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          Short meaning
          <input className={styles.input} value={shortLabel} onChange={(event) => setShortLabel(event.target.value)} />
        </label>
        <label className={styles.field}>
          Meaning
          <textarea className={styles.textarea} value={definition} onChange={(event) => setDefinition(event.target.value)} />
        </label>
        <label className={styles.field}>
          Russian hint <span className={styles.optional}>Optional</span>
          <input className={styles.input} value={hint} onChange={(event) => setHint(event.target.value)} />
        </label>
        <label className={styles.field}>
          Example
          <textarea className={styles.textarea} value={example} onChange={(event) => setExample(event.target.value)} />
        </label>
        <label className={styles.field}>
          Memory key <span className={styles.optional}>Optional</span>
          <input className={styles.input} value={memoryKey} onChange={(event) => setMemoryKey(event.target.value)} />
        </label>
        <div className={styles.field}>
          Common patterns
          <div className={styles.tags} style={{ marginTop: 9 }}>
            {collocations.map((item) => (
              <button
                key={item}
                className={styles.chip}
                type="button"
                onClick={() => setCollocations((list) => list.filter((value) => value !== item))}
              >
                {item} ×
              </button>
            ))}
          </div>
          <div className={styles.searchRow} style={{ gridTemplateColumns: "1fr auto", marginTop: 9 }}>
            <input
              className={styles.input}
              value={pattern}
              placeholder="Add a common pattern…"
              onChange={(event) => setPattern(event.target.value)}
            />
            <button
              className={styles.chip}
              type="button"
              onClick={() => {
                if (pattern.trim()) {
                  setCollocations((list) => [...list, pattern.trim()]);
                  setPattern("");
                }
              }}
            >
              Add
            </button>
          </div>
        </div>
        <div className={styles.field}>
          Similar words
          {relations.map((relation) => (
            <div key={relation.targetMeaningId} className={styles.banner}>
              <div>
                {relationTargetLabel(snapshot, relation.targetMeaningId) ?? "Meaning"}
                <textarea
                  className={styles.textarea}
                  value={relation.explanation}
                  onChange={(event) =>
                    setRelations((list) =>
                      list.map((item) =>
                        item.targetMeaningId === relation.targetMeaningId
                          ? { ...item, explanation: event.target.value }
                          : item,
                      ),
                    )
                  }
                />
                <button
                  className={styles.textButton}
                  type="button"
                  onClick={() => setRelations((list) => list.filter((item) => item.targetMeaningId !== relation.targetMeaningId))}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <input
            className={styles.input}
            placeholder="Search by meaning label…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {searchHits.map((hit) => (
            <button
              key={hit.id}
              className={styles.wordRow}
              type="button"
              onClick={() => {
                setRelations((list) =>
                  list.some((item) => item.targetMeaningId === hit.id)
                    ? list
                    : [...list, { targetMeaningId: hit.id, explanation: "" }],
                );
                setSearch("");
              }}
            >
              <span>
                {hit.label} <TypeTag type={hit.type} />
              </span>
            </button>
          ))}
        </div>
        <PrimaryButton onClick={() => void save()}>Save</PrimaryButton>
      </Screen>
    </Frame>
  );
}
